'use client'

import { useCallback, useMemo, useRef, useState } from 'react'
import { printMoney } from 'shopify-types/helpers'
import { addAlert, SetCreator as UISetCreator } from 'ui'
import { SetCreatorProduct } from 'ui/modules/SetCreator'

import { logger } from '@/helpers/logger'
import { MAX_PER_ITEM_IN_CART, useCart } from '@/store/cartStore'

type SetCreatorProps = {
  products: SetCreatorProduct[]
  typography: {
    buyButtonText: string
    addProductText: string
    discountText: string
    emptySelectedListText: string
    infoText?: string | null
    personalSetText: string
  }
  currency: string
  discountMap: Array<{
    minimumPrice: number
    discountPercentage: number
    discountCode: string
  }>
  accessibility: {
    deselectItemButton: string
    selectItemButton: string
    previousPrice: string
    currentPrice: string
    addedItem: string
    setItem: string
    price: string
    compareAtPrice: string
  }
}

const SetCreator = ({
  products,
  typography,
  currency,
  discountMap,
  accessibility,
}: SetCreatorProps) => {
  const [selectedVariants, setSelectedVariants] = useState<Array<SetCreatorProduct>>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const cart = useCart()

  const selectedVariantIndexRef = useRef(0)

  const { totalPrice, discountedPrice, discount } = useMemo(() => {
    const totalPrice = selectedVariants.reduce((accum, item) => accum + Number(item.price), 0)
    const discount = discountMap.reduce((accum, discount) => {
      return totalPrice >= discount.minimumPrice ? discount.discountPercentage : accum
    }, 0)

    const priceWithDiscount = Math.round(totalPrice * ((100 - discount) / 100) * 100) / 100

    const discountedPrice = printMoney({ amount: priceWithDiscount, currencyCode: currency })

    return {
      discount,
      totalPrice: printMoney({ amount: totalPrice, currencyCode: currency }),
      discountedPrice,
    }
  }, [currency, discountMap, selectedVariants])

  const onSelectVariant = useCallback((variant: SetCreatorProduct) => {
    setSelectedVariants(prev => [...prev, { ...variant, id: selectedVariantIndexRef.current++ }])
  }, [])

  const onDeselectVariant = useCallback((itemId?: number) => {
    if (itemId == null) return

    setSelectedVariants(prev => prev.filter(({ id }) => id !== itemId))
  }, [])

  const onBuyButtonClick = useCallback(async () => {
    setIsSubmitting(true)

    try {
      const priceWithoutDiscount = selectedVariants.reduce(
        (acc, item) => acc + Number(item.price),
        0,
      )

      const discountCode = discountMap.reduce(
        (acc, item) => (priceWithoutDiscount >= item.minimumPrice ? item.discountCode : acc),
        null as null | string,
      )

      if (discountCode != null) {
        await cart.applyDiscount([discountCode])
      }

      // Create array of additions with quantity from the selected variants
      const additions = selectedVariants
        .map(({ variantId }) => ({ variantId, quantity: 1 }))
        .reduce(
          (acc, curr) => {
            const elem = acc.find(item => item.variantId === curr.variantId)
            if (elem) {
              elem.quantity += 1
            } else {
              acc.push(curr)
            }
            return acc
          },
          [] as { variantId: string; quantity: number }[],
        )

      // Check if the quantity of the selected items, combined with the quantity of the items in the cart, exceeds the maximum quantity of 8
      const overMaxQuantity = additions.some(({ variantId, quantity }) => {
        const line = cart.data?.lines.find(line => line.merchandise.id === variantId)

        return line && line.quantity + quantity > MAX_PER_ITEM_IN_CART
      })

      if (overMaxQuantity) {
        addAlert.warning('Zulässige Höchstmenge an Waren 8')
      }

      // Add the items to the cart (quantity will be checked in the cart store)
      await cart.addToBasket(additions)

      cart.setIsOpen(true)
    } catch (e) {
      logger.error('SetCreator > onBuyButtonClick', e)

      addAlert.error('Fehler beim Hinzufügen eines Sets')
    } finally {
      setIsSubmitting(false)
    }
  }, [cart, discountMap, selectedVariants])

  return (
    <UISetCreator
      data={{
        variants: products,
        onBuyButtonClick,
        buyButtonText: typography.buyButtonText,
        addProductText: typography.addProductText,
        discountText: typography.discountText,
        personalSetText: typography.personalSetText,
        emptySelectedListText: typography.emptySelectedListText,
        infoText: typography.infoText,
        selectedVariants,
        onSelectVariant,
        onDeselectVariant,
        discount,
        discountedPrice,
        totalPrice,
        isSubmitting,
        accessibility,
      }}
    />
  )
}

export default SetCreator
